/**
 * Test cases for vessel auto-configuration logic
 * Run these tests to verify all auto-selection features work correctly
 */

import { devLog } from '../../../../../utils/devLog';
import * as AutoConfig from './vesselAutoConfig';

// Test helper to create a mock updateConfig function
const createMockUpdateConfig = () => {
  const calls = [];
  const fn = (updates) => calls.push(updates);
  fn.getCalls = () => calls;
  fn.getLastCall = () => calls[calls.length - 1];
  return fn;
};

// Test cases for vessel configuration auto-selection
export const testVesselAutoConfig = () => {
  const results = [];
  
  // Test 1: Vessel type change clears previous operations
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = {};
    AutoConfig.handleVesselTypeChange('ctv', vesselConfig, updateConfig);
    const result1 = updateConfig.getLastCall();
    
    AutoConfig.handleVesselTypeChange('rib', vesselConfig, updateConfig);
    const result2 = updateConfig.getLastCall();
    
    results.push({
      test: 'Vessel type change clears operations',
      passed: result1.operations?.includes('personnel_transfer') && 
              !result2.operations?.includes('personnel_transfer'),
      details: `CTV operations: ${result1.operations}, RIB operations: ${result2.operations}`
    });
  }
  
  // Test 2: PA/GA system auto-selects for ≥16 persons
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { optionalEquipment: [] };
    
    AutoConfig.handleMaxPersonsChange('17', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'PA/GA auto-selects for ≥16 persons',
      passed: result.optionalEquipment?.includes('pa_system'),
      details: `Equipment: ${result.optionalEquipment}`
    });
  }
  
  // Test 3: PA/GA system auto-selects for >750kW
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { optionalEquipment: [] };
    
    AutoConfig.handleEnginePowerChange('800', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'PA/GA auto-selects for >750kW',
      passed: result.optionalEquipment?.includes('pa_system'),
      details: `Equipment: ${result.optionalEquipment}`
    });
  }
  
  // Test 4: Passenger vessels get PA system and AIS
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { optionalEquipment: [], autoRequiredEquipment: [] };
    
    AutoConfig.handlePassengerCapacityChange('12', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Passenger vessels get PA system and AIS',
      passed: result.optionalEquipment?.includes('pa_system') && 
              result.autoRequiredEquipment?.includes('ais'),
      details: `Optional: ${result.optionalEquipment}, Auto-required: ${result.autoRequiredEquipment}`
    });
  }
  
  // Test 5: >24h operations get accommodation systems
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { optionalEquipment: [], maxPersons: 8 };
    
    AutoConfig.handleOperatingHoursChange('over_24h', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: '>24h operations get accommodation systems',
      passed: result.optionalEquipment?.includes('hvac') && 
              result.optionalEquipment?.includes('galley_equipment') &&
              result.optionalEquipment?.includes('watermaker'),
      details: `Equipment: ${result.optionalEquipment}`
    });
  }
  
  // Test 6: Below deck engines get fixed fire system
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { optionalEquipment: [] };
    
    AutoConfig.handleEnginePositionChange('below_deck', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Below deck engines get fixed fire system',
      passed: result.optionalEquipment?.includes('fixed_fire_system'),
      details: `Equipment: ${result.optionalEquipment}`
    });
  }
  
  // Test 7: Area category 0-2 gets offshore equipment
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { grossTonnage: 150, passengerCapacity: 0 };
    
    AutoConfig.handleCategoryChange('1', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Area 0-1 gets all offshore equipment',
      passed: result.autoRequiredEquipment?.includes('radar') &&
              result.autoRequiredEquipment?.includes('navtex') &&
              result.autoRequiredEquipment?.includes('epirb') &&
              result.autoRequiredEquipment?.includes('sart') &&
              result.autoRequiredEquipment?.includes('mf_radio') &&
              result.autoRequiredEquipment?.includes('gps') &&
              result.autoRequiredEquipment?.includes('echo_sounder'),
      details: `Equipment: ${result.autoRequiredEquipment}`
    });
  }
  
  // Test 8: Area category 3-4 gets coastal equipment
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { grossTonnage: 150, passengerCapacity: 0 };
    
    AutoConfig.handleCategoryChange('3', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Area 3 gets coastal equipment only',
      passed: result.autoRequiredEquipment?.includes('gps') &&
              result.autoRequiredEquipment?.includes('echo_sounder') &&
              !result.autoRequiredEquipment?.includes('radar') &&
              !result.autoRequiredEquipment?.includes('mf_radio'),
      details: `Equipment: ${result.autoRequiredEquipment}`
    });
  }
  
  // Test 9: AIS required for ≥300 GT
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { grossTonnage: 350, passengerCapacity: 0 };
    
    AutoConfig.handleCategoryChange('3', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'AIS required for ≥300 GT',
      passed: result.autoRequiredEquipment?.includes('ais'),
      details: `Equipment: ${result.autoRequiredEquipment}`
    });
  }
  
  // Test 10: Multi-purpose vessel allows manual operations selection
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = {};
    
    AutoConfig.handleVesselTypeChange('multi_purpose', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Multi-purpose vessel has empty operations',
      passed: result.operations?.length === 0,
      details: `Operations: ${result.operations}`
    });
  }
  
  // Test 11: Passenger vessel type triggers input prompt
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = {};
    
    AutoConfig.handleVesselTypeChange('pax', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Passenger vessel prompts for passenger capacity',
      passed: result.requiresPassengerInput === true && 
              result.passengerInputReminder?.includes('passenger capacity'),
      details: `Prompt: ${result.passengerInputReminder}`
    });
  }
  
  // Test 12: Entering passenger capacity triggers PA and AIS
  {
    const updateConfig = createMockUpdateConfig();
    const vesselConfig = { optionalEquipment: [], autoRequiredEquipment: [] };
    
    AutoConfig.handlePassengerCapacityChange('20', vesselConfig, updateConfig);
    const result = updateConfig.getLastCall();
    
    results.push({
      test: 'Passenger capacity triggers PA system and AIS',
      passed: result.optionalEquipment?.includes('pa_system') && 
              result.autoRequiredEquipment?.includes('ais') &&
              result.maxPersons >= 23, // 20 passengers + 3 crew
      details: `PA: ${result.optionalEquipment?.includes('pa_system')}, AIS: ${result.autoRequiredEquipment?.includes('ais')}, Max: ${result.maxPersons}`
    });
  }
  
  return results;
};

// Run tests and display results
export const runAllTests = () => {
  devLog('🧪 Running Vessel Auto-Configuration Tests...\n');
  
  const results = testVesselAutoConfig();
  const passed = results.filter(r => r.passed).length;
  const total = results.length;
  
  results.forEach((result, index) => {
    const icon = result.passed ? '✅' : '❌';
    devLog(`${icon} Test ${index + 1}: ${result.test}`);
    if (!result.passed) {
      devLog(`   Details: ${result.details}`);
    }
  });
  
  devLog(`\n📊 Results: ${passed}/${total} tests passed (${total} total tests)`);
  
  if (passed === total) {
    devLog('🎉 All tests passed! Auto-configuration is working correctly.');
  } else {
    devLog('⚠️  Some tests failed. Please review the auto-configuration logic.');
  }
  
  return { passed, total, results };
};

// Export for use in browser console
if (typeof window !== 'undefined') {
  window.testVesselConfig = runAllTests;
}